import * as React from "react";
import { useState } from "react";
import { useHistory, useParams } from "react-router";
import firebase from "firebase";
import {
  IonButton,
  IonCheckbox,
  IonContent,
  IonIcon,
  IonLabel,
  IonPage,
  IonSpinner,
  IonToast,
} from "@ionic/react";
import { downloadSharp } from "ionicons/icons";
import Panel from "../../components/ui/Panel";
import SelectModule from 'react-dropdown-select';
import {cjsDefault} from '../../shims/cjs-default';
import Header from "../../components/Header/Header";
import FileDownload from "../../components/FileDownload/FileDownload";
import ProbeDownload from "../../components/FileDownload/ProbeDownload";
import tourService from "../../services/tourService";
import firebaseService from "../../services/firebaseService";
import bigQueryService, {QueryJob} from "../../services/bigQueryService";
import formService from "../../services/formService";
import { getFireUser } from "../../firebaseConfig";
import { File } from "../../interfaces/File";
import { FormRequest } from "../../interfaces/FormRequest";
import { useForm } from "react-hook-form";
import {
  DataAttributes,
  DateTimes,
  SelectableFields,
  FileName,
} from "../../components/Forms/FormComponents";
import "./Form.css";
import Tour from "reactour";
import CountySelectorWithAlias from "../../components/Forms/CountySelectorWithAlias";
import { countiesWithAlias as countiesMO } from '../../assets/counties';
import { outOfStateCounties } from '../../assets/outOfStateCounties';

const Select = cjsDefault(SelectModule);

interface Attribute {
  name: string;
  checked: boolean;
  key: number;
}

interface IonCheckbox {
  value: string;
  checked: boolean;
}

const oneWeekAgo = new Date();
oneWeekAgo.setDate(oneWeekAgo.getDate() - 7);

// Severity options for CRASH query
const severityOptions = [
  { name: "Property Damage Only", value: ["PROPERTY DAMAGE ONLY"] },
  { name: "Suspected Serious Injury", value: ["SUSPECTED SERIOUS INJURY"] },
  { name: "Fatal", value: ["FATAL"] },
  { name: "Suspected Minor Injury", value: ["SUSPECTED MINOR INJURY"] }
];

// A day of statewide probe data is ~10 MB of speeds plus a 220 MB static
// file, and no county filter narrows it, so the range is where the size is
// decided. The other datasets are one to two orders of magnitude smaller.
const MAX_DAYS: Record<string, number> = {
  Probe: 31,
};
const DEFAULT_MAX_DAYS = 186;
// HERE is clustered by tmc, so scoping to counties cuts the scan as well as
// the download -- enough to allow a longer history than a statewide pull.
const PROBE_COUNTY_MAX_DAYS = 92;

// Helper to display "Crashes" instead of "Incidents" in UI
const getDisplayName = (form: string): string => {
  if (form === "Incidents") return "Crashes";
  return form;
};

const Data: React.FC = () => {
  const { form } = useParams<{ form: string }>();
  const displayName = getDisplayName(form);
  let attributeOptions: Attribute[] = [];
  attributeOptions = formService.determineFormValues(attributeOptions, form);
  const formColor = formService.getFormColor();
  const [queryResponse, setQueryResponse] = useState(false);
  const [errorResponse, setErrorResponse] = useState(false);
  const [responseRequested, setResponseRequested] = useState(false);
  const [checks, setChecks] = useState<Attribute[]>([]);
  const [file, setFile] = useState<string>(form);
  const [counties, setCounties] = useState<{ name: string; value: string[] }[]>([]);
  const [severity, setSeverity] = useState<{ name: string; value: string[] }[]>([]);
  const [formError, setFormError] = useState(false);
  const [emptyResponse, setEmptyResponse] = useState(false);
  const [queryError, setQueryError] = useState("");
  const [dateError, setDateError] = useState(false);
  const [fileError, setFileError] = useState(false);
  const [startSelectedDate, handleStartDateChange] = useState<Date | null>(
    oneWeekAgo
  );
  const [endSelectedDate, handleEndDateChange] = useState<Date | null>(
    new Date()
  );
  const [unit, setUnit] = useState<number>(60);
  const [interval, setInterval] = useState<number>(60);
  const { handleSubmit } = useForm<FormRequest>();
  const findUser = getFireUser();
  let uid = "";
  if (findUser != null) {
    uid = findUser.uid;
  }

  const [downloadableFiles, setDownloadableFiles] = useState<File[]>([]);
  const [probeUrls, setProbeUrls] = useState<string[]>([]);
  const [staticUrl, setStaticUrl] = useState<string>("");
  // An export runs for minutes, so the form says which stage it is in rather
  // than spinning silently.
  const [jobStage, setJobStage] = useState<string>("");
  // Downloads are signed per job, so the results panel needs to know which one.
  const [jobId, setJobId] = useState<string>("");

  React.useEffect(() => {
    firebaseService.getFiles().onSnapshot((docs) => {
      const downloadedFiles: File[] = [];
      docs.forEach((doc: firebase.firestore.QueryDocumentSnapshot) => {
        const data = doc.data();
        if (data != null) {
          let typeToFormName = "";
          switch (data.type) {
            case "inrix_probe_query":
              typeToFormName = "Probe";
              break;
            case "waze_incident_query":
              typeToFormName = "WazeIncident";
              break;
            case "waze_jam_query":
              typeToFormName = "WazeJam";
              break;
            case "transcore_incident_query":
              typeToFormName = "Incidents";
              break;
            case "transcore_detector_query":
              typeToFormName = "Detector";
              break;
            case "counts_query":
              typeToFormName = "Counts";
          
              break;
          }
          if (typeToFormName === form) {
            downloadedFiles.push(
              new File(
                data.uid,
                doc.id,
                data.location,
                data.type,
                data.ready,
                data.queryResponse,
                data.queryDate
              )
            );
          }
        }
      });
      setDownloadableFiles(downloadedFiles);
    });
    setQueryResponse(false);
    setResponseRequested(false);
    setChecks([]);
    // Probe starts statewide -- no county preselected -- because picking one
    // for the user would silently narrow an export they did not scope.
    setCounties(form === "Probe" ? [] : getCountyList(form).slice(0, 1));
    setSeverity([]);
    setFile(form);
    setFormError(false);
    setDateError(false);
    setFileError(false);
    handleStartDateChange(oneWeekAgo);
    handleEndDateChange(new Date());
    setUnit(60);
    setInterval(60);
    setProbeUrls([]);
    setStaticUrl("");
    setJobStage("");
    setJobId("");
  }, [uid, form]);

  const setCheck = (e: CustomEvent<IonCheckbox>) => {
    let currentCheckArr = checks;
    if (e.detail.checked) {
      let needsInsertion = false;
      if (currentCheckArr.length > 0) {
        for (const check of currentCheckArr) {
          if (check.name === e.detail.value) {
            needsInsertion = false;
            break;
          } else {
            needsInsertion = true;
          }
        }
      } else {
        needsInsertion = true;
      }

      if (needsInsertion) {
        const newCheck: Attribute = attributeOptions.filter(
          (attribute) => attribute.name === e.detail.value
        )[0];
        currentCheckArr.push(newCheck);
      }
    } else {
      currentCheckArr = currentCheckArr.filter(
        (attribute) => attribute.name !== e.detail.value
      );
      setChecks(currentCheckArr);
    }
    setChecks(currentCheckArr);
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  function getCountyList(form:string) {
    // For DETECTOR and CRASHES, add "All Counties" option at the beginning
    if (form === "Detector" || form === "Incidents") {
      return [{ name: "All Counties", value: ["ALL"] },...countiesMO];
    }
    // Probe covers the metros either side of the state line, so its counties
    // are not only Missouri's.
    if (form === "Probe") {
      return [...countiesMO, ...outOfStateCounties];
    }
    return countiesMO;
}

  const onSubmit = (data: FormRequest) => {
    const scoped = form === "Probe" && counties.length > 0;
    const maxDays = scoped
      ? PROBE_COUNTY_MAX_DAYS
      : MAX_DAYS[form] ?? DEFAULT_MAX_DAYS;
    if (startSelectedDate && endSelectedDate) {
      const days = (endSelectedDate.getTime() - startSelectedDate.getTime()) / 86400000;
      if (days > maxDays) {
        setQueryError(
          `${displayName} queries are limited to ${maxDays} days at a time. ` +
          `This range is ${Math.ceil(days)} days.`
        );
        setErrorResponse(true);
        return;
      }
    }
    setResponseRequested(true);
    const convertChecks: number[] = [];
    checks.forEach((check) => {
      convertChecks.push(check.key);
    });
    data.attributes = convertChecks;
    // Add severity for CRASH (Incidents) form
    if (form === "Incidents" && severity.length > 0) {
      data.severity = severity.flatMap(s => s.value);
    }
    const validatedData = formService.validateForm(
      data,
      startSelectedDate,
      endSelectedDate,
      file,
      counties,
      downloadableFiles,
      convertChecks,
      interval,
      unit,
      form,
      setFormError,
      setDateError,
      setFileError
    );

    if (formError || dateError || fileError) {
      setResponseRequested(false);
    }

    if (validatedData != null) {
      data = validatedData;
      console.log("Form validated, submitting:", form, data);
      // All three forms run the same way now: submit, watch the job, take
      // whatever files it produced.
      const runners: Record<string, (req: FormRequest, onProgress: (msg: string) => void) => Promise<QueryJob>> = {
        Probe: bigQueryService.queryProbeData,
        Incidents: bigQueryService.queryCrashData,
        Detector: bigQueryService.queryDetectorData,
      };
      const run = runners[form];
      if (!run) {
        setErrorResponse(true);
        setResponseRequested(false);
        return;
      }

      setProbeUrls([]);
      setStaticUrl("");
      setJobId("");
      run(data, setJobStage)
        .then((job) => {
          // A query that matched nothing still exports a file -- the header
          // row on its own -- so say so rather than offering an empty CSV.
          const urls = job.is_empty
            ? []
            : job.final_csv_url ? [job.final_csv_url] : job.csv_urls ?? [];
          if (job.is_empty || (urls.length === 0 && !job.static_csv_url)) {
            setEmptyResponse(true);
          }
          if (urls.length > 0 || job.static_csv_url) {
            setJobId(job.id ?? job.job_id ?? "");
            setProbeUrls(urls);
            setStaticUrl(job.static_csv_url ?? "");
            setQueryResponse(urls.length > 0);
          }
          setErrorResponse(false);
          setJobStage("");
          setResponseRequested(false);
        })
        .catch((err) => {
          console.error(`${form} query failed:`, err);
          setQueryError(err.message ?? "");
          setErrorResponse(true);
          setJobStage("");
          setResponseRequested(false);
        });
    } else {
      setResponseRequested(false);
    }
  };
  const steps = tourService.getStepsFor("QueryData");
  const isTour = tourService.StartTour();
  const history = useHistory();
  return (
    <IonPage>
      <Header title={"Query " + displayName} />
      <IonContent fullscreen className="page-content">
        <div
          className="page-body page-body--narrow"
          style={{ ["--accent" as string]: `var(--db-${formColor || "grey"})` } as React.CSSProperties}
        >
          <div className="page-body__meta">
            <h1 className="page-body__title">{"Query " + displayName + " Database"}</h1>
            <span>Results appear below once the query finishes</span>
          </div>

          <Panel
            className="first-step"
            title="Query parameters"
            subtitle="Choose a date range and the fields you need"
            accent={`var(--db-${formColor || "grey"})`}
            icon={<IonIcon icon={downloadSharp} />}
          >
            <form onSubmit={handleSubmit(onSubmit)}>
              {form === "Probe" && (
                <p className="card-note">
                  Pick counties to scope the query. Statewide is about 10 MB of
                  speeds per day queried plus a 220 MB file of segment
                  metadata, and is limited to {MAX_DAYS.Probe} days; scoped to
                  counties it is a fraction of that, and you can ask for up to{" "}
                  {PROBE_COUNTY_MAX_DAYS} days.
                </p>
              )}
              <DateTimes
                startDate={startSelectedDate}
                endDate={endSelectedDate}
                handleStartDateChange={handleStartDateChange}
                handleEndDateChange={handleEndDateChange}
                form={form}
              />
              {/* {attributeOptions.length > 0 && (
                <DataAttributes
                  attributeOptions={attributeOptions}
                  setCheck={setCheck}
                  form={form}
                />
              )} */}
              {(form === "Probe" ||
                form === "Detector" ||
                form === "Counts") && (
                <SelectableFields
                  unit={unit}
                  interval={interval}
                  setUnit={setUnit}
                  setInterval={setInterval}
                  form={form}
                  hideUnitInTime={(form==="Counts")}
                />
              )}
              <FileName file={file} setFile={setFile} form={form} />
              {/* Optional for Probe, where no counties means statewide. */}
              {(
                <CountySelectorWithAlias
                  counties={counties}
                  setCounties={setCounties}
                  options={getCountyList(form)}
                  type="counties"
                  width="county-select"
                />
              )}
              {/* Severity selector - only for CRASH (Incidents) */}
              {form === "Incidents" && (
                <div className="form-div county-column">
                  <IonLabel>Select Severity</IonLabel>
                  <div className="county-form-div">
                    <Select
                      className="county-select"
                      options={severityOptions}
                      values={severity}
                      multi={true}
                      labelField="name"
                      valueField="value"
                      onChange={setSeverity}
                    />
                  </div>
                </div>
              )}
              <IonButton
                className="fifth-step btn-accent"
                type="submit"
                disabled={responseRequested}
              >
                Submit Query
              </IonButton>
              {responseRequested && (
                <div className="response-requested">
                  {jobStage
                    ? `Working: ${jobStage}`
                    : "Preparing your data download request..."}
                  &nbsp;
                  <IonSpinner />
                </div>
              )}
              <IonToast
                isOpen={queryResponse}
                onDidDismiss={() => setQueryResponse(false)}
                color="light"
                message="Requested files are available for download now!"
                buttons={[
                  {
                    text: "Ok",
                    side: "end",
                    role: "cancel",
                  },
                ]}
              />
              <IonToast
                isOpen={emptyResponse}
                onDidDismiss={() => setEmptyResponse(false)}
                color="light"
                message="That query finished with no matching rows. Try a wider date range, or fewer filters."
                duration={10000}
              />
              <IonToast
                isOpen={errorResponse}
                onDidDismiss={() => setErrorResponse(false)}
                color="danger"
                message={queryError || "Oops. Looks like our data collection server is down right now. Please try again later!"}
                cssClass="toast-error"
                duration={10000}
              />
              <IonToast
                isOpen={fileError}
                onDidDismiss={() => setFormError(false)}
                color="danger"
                message="Invalid file name. File names must be under 25 characters and cannot match another existing file."
                cssClass="toast-error"
                duration={10000}
              />
              <IonToast
                isOpen={formError}
                onDidDismiss={() => setFormError(false)}
                color="danger"
                message="Please fill out all form fields"
                cssClass="toast-error"
                duration={10000}
              />
              <IonToast
                isOpen={dateError}
                onDidDismiss={() => setDateError(false)}
                color="danger"
                message="The specified date range is invalid"
                cssClass="toast-error"
                duration={10000}
              />
            </form>
          </Panel>
          <FileDownload downloadableFiles={downloadableFiles} />

        {/* Probe Download Links Section */}
        <ProbeDownload
          staticUrl={staticUrl}
          dynamicUrls={probeUrls}
          form ={form}
          jobId={jobId}
          onClear={() => {
            setProbeUrls([]);
            setStaticUrl("");
            setJobId("");
          }}
        />

        </div>

        <Tour
          steps={steps}
          isOpen={isTour}
          accentColor="black"
          onRequestClose={() => {
            tourService.GoBack(history);
          }}
        />
      </IonContent>
    </IonPage>
  );
};

export default Data;